import { css } from 'styled-components/macro';

// Shared design tokens used by every council theme.
// Council themes (src/contexts/Theme.js) only set colours, fonts and logos.
// 1rem = 10px (see src/index.css)

// spacing scale: 4px steps at the small end, then 8px+
export const space = {
  0: '0',
  1: '0.4rem',
  2: '0.8rem',
  3: '1.2rem',
  4: '1.6rem',
  5: '2.4rem',
  6: '3.2rem',
  7: '4.8rem',
  8: '6.4rem',
};

export const fontSize = {
  xs: '1.2rem',
  sm: '1.4rem',
  md: '1.6rem',
  lg: '1.8rem',
  xl: '2.2rem',
  xxl: '2.8rem',
  display: '3.4rem',
};

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  heavy: 800,
};

export const lineHeight = {
  tight: 1.25,
  normal: 1.5,
};

export const radius = {
  sm: '0.6rem',
  md: '1rem',
  lg: '1.6rem',
  pill: '999px',
};

export const shadow = {
  sm: '0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.1)',
  md:
    '0 4px 8px -2px rgba(16, 24, 40, 0.1), 0 2px 4px -2px rgba(16, 24, 40, 0.06)',
  lg:
    '0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03)',
  up: '0 -4px 16px rgba(16, 24, 40, 0.08)',
};

// neutral palette shared by all themes
export const neutral = {
  surface: '#FFFFFF',
  canvas: '#F5F6F8',
  subtle: '#EEF0F3',
  border: '#E1E4EA',
  borderStrong: '#C4C9D2',
  textMuted: '#5D6575',
  focus: '#1A5FD6',
};

export const breakpoints = {
  sm: 500,
  md: 700, // matches useWindowSize isMobile and the nav switch from top bar to sidebar
  lg: 900,
  xl: 1200,
};

export const media = Object.keys(breakpoints).reduce((acc, key) => {
  acc[key] = (...args) => css`
    @media only screen and (min-width: ${breakpoints[key]}px) {
      ${css(...args)}
    }
  `;
  return acc;
}, {});

export const layout = {
  gutter: space[4],
  gutterWide: space[6],
  contentMaxWidth: '112rem',
  readableMaxWidth: '72rem',
  touchTarget: '4.4rem',
  // nav sizes are relied on by the chat and whiteboard layouts, change with care
  mobileNavHeight: 'max(8.5vh, 67px)',
  sidebarWidth: 'max(10vw, 120px)',
};

export const zIndex = {
  nav: 10,
  overlay: 100,
};

export const transition = {
  fast: '120ms ease-out',
  normal: '200ms ease-out',
};

// visible keyboard focus without showing a ring on mouse clicks
export const focusRing = css`
  &:focus-visible {
    outline: 3px solid ${neutral.focus};
    outline-offset: 2px;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

const tokens = {
  space,
  fontSize,
  fontWeight,
  lineHeight,
  radius,
  shadow,
  neutral,
  breakpoints,
  layout,
  zIndex,
  transition,
};

export default tokens;
